import { z } from 'zod';
import { counter } from './counts.js';
import { pageOf } from './paging.js';
import { emailSchema } from './signUp.js';
/**
 * The three read-only listings: suppressions, sends, and the consent record.
 *
 * One page shape for all three, shared with every other listing in `pageOf`.
 * They are the same question — "what happened to this mail" — asked from three
 * angles, and giving each its own paging fields would be three places for the
 * same arithmetic to drift.
 */
const iso = z.string().datetime();
export const suppressionRowSchema = z.object({
    email: emailSchema,
    /** Kept apart rather than collapsed: each has a different fix. */
    reason: z.enum(['unsubscribed', 'hard_bounce', 'complaint', 'manual']),
    createdAt: iso,
});
export const suppressionPageSchema = pageOf(suppressionRowSchema);
export const sendRowSchema = z.object({
    messageId: z.string(),
    /** `unknown` is not a failure. It may already have been delivered. (I7) */
    status: z.enum(['accepted', 'duplicate', 'rejected', 'failed', 'unknown']),
    provider: z.string(),
    providerMessageId: z.string().nullable(),
    /** The provider's own sentence — the column that makes this screen useful. */
    reason: z.string().nullable(),
    createdAt: iso,
});
export const sendPageSchema = pageOf(sendRowSchema);
export const consentRowSchema = z.object({
    email: emailSchema,
    /** Exactly what `consent_event_type` can hold. Kept in step with the enum. */
    type: z.enum([
        'imported',
        'signup_requested',
        'confirmation_sent',
        'confirmed',
        'unsubscribe_requested',
        'unsubscribed',
        'suppression_removed',
    ]),
    source: z.string(),
    ip: z.string().nullable(),
    occurredAt: iso,
});
export const consentPageSchema = pageOf(consentRowSchema);
/**
 * One day of sending.
 *
 * Counts rather than rates, because a rate per day averaged is not the rate
 * over the period: a day with three sends and one bounce is 33%, and averaging
 * that against a day of forty thousand produces a figure describing neither.
 * The screen divides the totals it needs.
 */
export const sendingDaySchema = z.object({
    day: z.iso.datetime(),
    accepted: counter,
    rejected: counter,
    failed: counter,
    /** Ambiguous. Never counted as a failure, and never as a success. (I7) */
    unknown: counter,
    bounced: counter,
    complained: counter,
});
export const sendingHistorySchema = z.object({ days: z.array(sendingDaySchema) });
/**
 * Suppressing an address by hand.
 *
 * The note is required, and required for a reason: a manual suppression comes
 * from something that happened outside this product — a reply, a phone call, a
 * request in person — and this sentence is the only trace of it that will ever
 * exist. An unexplained entry is one nobody dares remove and nobody can defend.
 */
export const suppressRequestSchema = z.object({
    /**
     * One or more addresses, under one note.
     *
     * Several because the two screens that suppress by hand ask the same thing:
     * the deliverability list adds an address somebody read off an email, and the
     * contacts list adds the people somebody has just ticked. Two request shapes
     * would be two places for the consent record to be written differently, and
     * the record is the whole reason this goes through a use case.
     *
     * Bounded at a hundred because a person can only tick what they can see, and
     * a page is fifty. It is a cap on one request fanning out into thousands of
     * writes, not a limit anybody will meet by hand.
     */
    emails: z.array(z.string()).min(1, 'choose at least one address').max(100),
    /**
     * Whatever happened, happened outside this product, and this sentence is the
     * only trace of it there will ever be. Required for that reason.
     */
    note: z
        .string()
        .trim()
        .min(1, 'say why, so somebody reading this in a year knows what happened')
        .max(500),
});
/**
 * `not-suppressed` is the same answer as already-removed, deliberately:
 * pressing the button twice is one removal rather than an error.
 */
export const unsuppressResponseSchema = z.object({
    status: z.enum(['removed', 'not-suppressed']),
    /** What it was on the list for, so the screen can say what was undone. */
    was: z.enum(['unsubscribed', 'hard_bounce', 'complaint', 'manual']).nullable(),
});
//# sourceMappingURL=deliverability.js.map